import type {ReactElement} from 'react';
import React, {useMemo, useRef, useState} from 'react';
import {Dropdown} from '@Components/dropdown-v2';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {onOpenSettingsModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {SkeletonBrick, SkeletonBrickTheme} from '@Components/skeleton-brick';
import NavDropdownSelector from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-dropdown-selector/nav-dropdown-selector';
import {
  copyPoster,
  logoutUserFromEmbeddedEditor,
  onClickOpenADesign,
  openNewDesign,
  shouldDisplaySubmitAsTemplateButton,
  submitAsTemplateCallback,
  onClickOpenVersionHistoryPanel,
  showVersionHistoryPulse,
  markVersionHistoryPulseAsSeen,
} from '@Components/poster-editor/library/poster-editor-library';
import {isUserLoggedIn} from '@Libraries/user.library';
import {useAppSelector} from '@/hooks';
import styles from './nav-file-dropdown.module.scss';
import {useShowContextualSkeletons} from '@Hooks/poster-editor/useShowContextualSkeletons';
import {NavFileLanguageOption} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-file-language-option/nav-file-language-option';
import {NavSettingsOption} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-settings-option/nav-settings-option';
import {trackPosterBuilderGA4Events, GA4EventParamName, GA4EventName, GA4EventSources} from '@Libraries/ga-events';

enum NavFileDropdownOptions {
  NEW = 'new',
  DUPLICATE = 'duplicate',
  OPEN = 'open',
  VERSION_HISTORY = 'version_history',
  LANGUAGE = 'language',
  SETTINGS = 'settings',
  LOGOUT = 'logout',
  NEED_HELP = 'need_help',
}

export function NavFileDropdown(): ReactElement<any> | null {
  const showSkeleton = useShowContextualSkeletons();
  const [isOpened, setIsOpened] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const posterHashedId = useAppSelector((state) => {
    return state.posterEditor.posterObject?.hashedID;
  });

  const getSelector = (): ReactElement<any> => {
    return <NavDropdownSelector ref={buttonRef} text={window.i18next.t('pmwjs_file')} />;
  };

  const getEmbeddedEditorLogoutBtnClassName = (): string => {
    return isUserLoggedIn() ? 'embedded-show' : '_hidden';
  };

  const isVersionHistoryEnabled = (): boolean => {
    return isUserLoggedIn() && posterHashedId !== '';
  };

  const handleVersionHistoryClick = (): void => {
    if (isVersionHistoryEnabled()) {
      if (showVersionHistoryPulse()) {
        markVersionHistoryPulseAsSeen();
      }
      trackPosterBuilderGA4Events(GA4EventName.VERSION_HISTORY_OPEN, {
        [GA4EventParamName.SOURCE]: GA4EventSources.NAV_FILE_DROPDOWN,
      });
      void onClickOpenVersionHistoryPanel();
    }
  };

  const getLatterListItems = (): ControlledListItemType[] => {
    return [
      {
        id: NavFileDropdownOptions.LOGOUT,
        text: window.i18next.t('pmwjs_logout'),
        icon: 'icon-sign-in',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        className: getEmbeddedEditorLogoutBtnClassName(),
      },
      {
        id: NavFileDropdownOptions.NEED_HELP,
        text: window.i18next.t('pmwjs_need_help'),
        icon: 'icon-help',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      },
    ];
  };

  const getInitialListItems = (): ControlledListItemType[] => {
    return [
      {
        id: NavFileDropdownOptions.NEW,
        icon: 'icon-new-design',
        text: window.i18next.t('pmwjs_new'),
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      },
      {
        id: NavFileDropdownOptions.DUPLICATE,
        text: window.i18next.t('pmwjs_duplicate'),
        icon: 'icon-copy',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      },
      {
        id: NavFileDropdownOptions.OPEN,
        text: window.i18next.t('pmwjs_open'),
        icon: 'icon-multiple-layouts',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        className: 'embedded-hidden',
      },
      {
        id: NavFileDropdownOptions.VERSION_HISTORY,
        text: window.i18next.t('pmwjs_version_history'),
        icon: 'icon-history',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        className: 'embedded-hidden',
        isDisabled: !isVersionHistoryEnabled(),
        hasPulsatingDot: showVersionHistoryPulse(),
      },
    ];
  };

  const onClickHandler = async (clickedId: NavFileDropdownOptions): Promise<void> => {
    switch (clickedId) {
      case NavFileDropdownOptions.DUPLICATE:
        copyPoster();
        break;
      case NavFileDropdownOptions.NEW:
        openNewDesign();
        break;
      case NavFileDropdownOptions.OPEN:
        onClickOpenADesign();
        break;
      case NavFileDropdownOptions.VERSION_HISTORY:
        handleVersionHistoryClick();
        break;
      case NavFileDropdownOptions.LOGOUT:
        void logoutUserFromEmbeddedEditor();
        break;
      case NavFileDropdownOptions.NEED_HELP:
        window.open('https://support.postermywall.com/hc/en-us/articles/360058560052?utm_source=editor&utm_medium=nav');
        break;
      case NavFileDropdownOptions.LANGUAGE:
        onOpenSettingsModal();
        break;
      case NavFileDropdownOptions.SETTINGS:
        onOpenSettingsModal();
        break;
      default:
        throw new Error('unhandled option in nav file dropdown');
    }
  };

  const getLanguageDropdown = (): ReactElement<any> => {
    return <NavFileLanguageOption />;
  };

  const getSettingsDropdown = (): ReactElement => {
    return <NavSettingsOption />;
  };

  const getList = (): ReactElement<any> => {
    return (
      <>
        <ControlledList
          className="spacing-m-b-2"
          onClick={(id: string): void => {
            void onClickHandler(id as NavFileDropdownOptions);
          }}
          items={getInitialListItems()}
        />
        {getLanguageDropdown()}
        {getSettingsDropdown()}
        <ControlledList
          className="spacing-m-b-2"
          onClick={(id: string): void => {
            void onClickHandler(id as NavFileDropdownOptions);
          }}
          items={getLatterListItems()}
        />
      </>
    );
  };

  const getSubmitAsTemplateButton = (): ReactElement<any> => {
    return (
      <Button
        customClasses="_full-width spacing-m-b-2"
        iconPlacement={IconPlacement.RIGHT}
        icon="icon-submit-design"
        size={Size.SMALL}
        type={Type.SECONDARY}
        text={window.i18next.t('pmwjs_submit_as_template')}
        onClick={submitAsTemplateCallback}
      />
    );
  };

  const getPopup = (): ReactElement<any> => {
    return (
      <div className={styles.popupContainer}>
        {getList()}
        {shouldDisplaySubmitAsTemplateButton() ? getSubmitAsTemplateButton() : null}
      </div>
    );
  };

  return useMemo((): ReactElement<any> => {
    return showSkeleton ? (
      <div className={styles.skeletonContainer}>
        <SkeletonBrick theme={SkeletonBrickTheme.DARK} width={22} height={20} />
      </div>
    ) : (
      <Dropdown
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        popUpHasCustomWidth
        popUpHasCustomHeight
        popupOffset={8}
        position={DROPDOWN_POSITION.FIXED}
        maxHeight={400}
        selector={getSelector()}
        popup={getPopup()}
        className="spacing-m-r-2"
        popupSpacingClass="spacing-p-r-2 spacing-p-t-2 spacing-p-l-2"
        onOpen={(): void => {
          if (!isOpened) {
            setIsOpened(true);
          }
        }}
        onClose={(): void => {
          if (isOpened) {
            setIsOpened(false);
            buttonRef?.current?.blur();
          }
        }}
      />
    );
  }, [showSkeleton, posterHashedId]);
}
